import { $trigger } from '../../y/events';
import { $el } from '../elements';
import { UIClassesSpec } from '../types';
import { UIViewAddSpec } from '../view';
import { UITextBasedInputOptions, UITextBasedInput } from './input-base';

const emptyRegex = /^\s*$/
const rulerRegex = /^\s*___+\s*$/

function escapeHTML(text: string) {
    const map: { [c: string]: string } = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;'
    };

    return text.replace(/[&<>]/g, m => map[m]);
}

function processText(text: string) {
    return text.replace(/(^| |_|>)\*([^*]+)\*/g, '$1<b>$2</b>').replace(/(^| |\*|>)_([^_]+)_/g, '$1<i>$2</i>').
        replace(/\[([^\[\]]+)\]\(([^()<>]+)\)/g, '<a href="$2" class="ui-markdown-link u-link">$1</a>');
}

export function uiMarkdown(src: string): UIViewAddSpec {
    const elements: HTMLElement[] = [];
    let in_paragraph = false;
    let current = '';

    function emit() {
        if(in_paragraph)
            pushTag('p', 'p-f-text b b-text', current, true);

        in_paragraph = false;
        current = '';
    }

    function pushTag(tag: string, classes?: UIClassesSpec, content?: string, no_emit?: boolean) {
        if(!no_emit)
            emit();

        const elem = $el.create(tag, classes);
        if(content)
            elem.innerHTML = content;

        elements.push(elem)
    }

    function pushText(text: string) {
        if(!in_paragraph) {
            emit();
            in_paragraph = true;
        }

        if(current.length)
            current += '<br>';
        current += processText(text);
    }

    const lines = escapeHTML(src).split('\n');

    for(const l of lines) {
        if(emptyRegex.test(l)) {
            emit();
        } else if(rulerRegex.test(l)) {
            pushTag('hr', 'm-short');
        } else if(l.startsWith('# ')) {
            pushTag('h3', 'p-f-heading b b-subtitle', processText(l.substring(2)));
        } else if(l.startsWith('## ')) {
            pushTag('h4', 'p-f-subheading b b-subtitle', processText(l.substring(3)));
        } else if(l.startsWith('### ')) {
            pushTag('h5', 'p-f-text t-w-bold b b-subtitle', processText(l.substring(4)));
        } else if(l.startsWith('- ')) {
            pushTag('p', 'p-l-1 p-f-text b b-text', processText(l.substring(2)));
        } else {
            pushText(processText(l));
        }
    }

    emit();

    return elements;
}

interface UIMarkdownInputOptions extends UITextBasedInputOptions<string> {
}

export class UIMarkdownInput extends UITextBasedInput<string> {
    private _value: string;
    private _preview: HTMLElement;

    constructor(options: UIMarkdownInputOptions = {}) {
        options.lines = options.lines || 5;
        super(options);
        this.classList.add('ui-markdown-input');
        const preview = this._preview = $el.DIV('r-preview')
        this.ui_add(preview);
        preview.addEventListener('click', () => { this.focus() });

        this._value = '';

        this.doCheck();
    }

    protected onText(text: string, notify: boolean) {
        this._value = text;
        $trigger(this, 'value', text);
        if(notify)
            $trigger(this, 'input');

        this.fixDesign();
        this.doCheck();
    }

    protected doCheck() {
        this.setOkay(!(this.required && !this._value));
    }

    get value() {
        return this._value;
    }

    set value(value: string) {
        this.setText(value || '', false);
        this._updatePreview();
    }

    private _updatePreview() {
        this._preview.ui_empty(uiMarkdown(this._value));
    }

    override onBlur() {
        this._updatePreview();
    }
}
